import { useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { useSettings } from "@/lib/SettingsContext";

export default function NewsletterSignupForm({ source = "footer", variant = "dark" }) {
  const { settings } = useSettings();
  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [website, setWebsite] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error("Please enter an email.");
      return;
    }
    setSubmitting(true);
    try {
      // Always store in our DB.
      await api.post("/newsletter", {
        email,
        first_name: firstName || null,
        source,
        website,
      });

      // If Mailchimp is configured, also POST to that action URL
      // using a hidden iframe so we do not get blocked by CORS.
      if (settings.mailchimp_action_url) {
        try {
          const form = document.createElement("form");
          form.action = settings.mailchimp_action_url;
          form.method = "POST";
          form.target = "wt-mc-frame";
          form.style.display = "none";
          const fields = { EMAIL: email };
          if (firstName) fields.FNAME = firstName;
          Object.entries(fields).forEach(([k, v]) => {
            const input = document.createElement("input");
            input.type = "hidden";
            input.name = k;
            input.value = v;
            form.appendChild(input);
          });
          let frame = document.getElementById("wt-mc-frame");
          if (!frame) {
            frame = document.createElement("iframe");
            frame.name = "wt-mc-frame";
            frame.id = "wt-mc-frame";
            frame.style.display = "none";
            document.body.appendChild(frame);
          }
          document.body.appendChild(form);
          form.submit();
          setTimeout(() => form.remove(), 1500);
        } catch (_) {
          // ignore best-effort secondary submit
        }
      }

      setDone(true);
      setEmail("");
      setFirstName("");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not subscribe. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const dark = variant === "dark";

  if (done) {
    return (
      <p
        data-testid={`newsletter-thanks-${source}`}
        className="mt-4 text-sm"
        style={{ color: dark ? "rgba(250,247,240,0.92)" : "var(--wt-primary)" }}
      >
        You are on the list. Thank you.
      </p>
    );
  }

  return (
    <form
      data-testid={`newsletter-form-${source}`}
      onSubmit={onSubmit}
      className="mt-4 flex flex-col gap-2"
      noValidate
    >
      <div className="hidden" aria-hidden="true">
        <input
          type="text"
          tabIndex={-1}
          autoComplete="off"
          value={website}
          onChange={(e) => setWebsite(e.target.value)}
          data-testid={`newsletter-honeypot-${source}`}
        />
      </div>
      <input
        type="text"
        placeholder="First name (optional)"
        value={firstName}
        onChange={(e) => setFirstName(e.target.value)}
        data-testid={`newsletter-firstname-${source}`}
        className="h-11 rounded-md px-3 text-sm"
        style={
          dark
            ? {
                backgroundColor: "rgba(250,247,240,0.08)",
                color: "var(--wt-bg)",
                border: "1px solid rgba(250,247,240,0.18)",
              }
            : {
                backgroundColor: "#fff",
                color: "var(--wt-text)",
                border: "1px solid var(--wt-border)",
              }
        }
      />
      <div className="flex gap-2">
        <input
          type="email"
          required
          placeholder="Email address"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          data-testid={`newsletter-email-${source}`}
          className="flex-1 h-11 rounded-md px-3 text-sm"
          style={
            dark
              ? {
                  backgroundColor: "rgba(250,247,240,0.08)",
                  color: "var(--wt-bg)",
                  border: "1px solid rgba(250,247,240,0.18)",
                }
              : {
                  backgroundColor: "#fff",
                  color: "var(--wt-text)",
                  border: "1px solid var(--wt-border)",
                }
          }
        />
        <button
          type="submit"
          disabled={submitting}
          data-testid={`newsletter-submit-${source}`}
          className="h-11 px-4 rounded-md text-sm font-semibold inline-flex items-center justify-center gap-2"
          style={
            dark
              ? { backgroundColor: "var(--wt-bg)", color: "var(--wt-ground)" }
              : { backgroundColor: "var(--wt-primary)", color: "var(--wt-bg)" }
          }
        >
          {submitting && <Loader2 size={14} className="animate-spin" />}
          {submitting ? "" : "Subscribe"}
        </button>
      </div>
    </form>
  );
}
